/**
 * The stage's drag/zoom model (2026-09-02).
 *
 * User: "Make 3d stage interactive and possible to rotate character and so
 * on."
 *
 * ## It turns the delver, not the camera
 *
 * The obvious reading is an orbit camera, and it is the wrong one here. The
 * stage is not a model viewer floating in void — `stage-set.ts` builds a real
 * three-walled alcove out of the dungeon's own geometry, with two torches on
 * the wall casting the light. Orbiting past about 70 degrees puts the camera
 * outside that alcove, looking at the character's back through a wall, lit
 * from behind by torches now facing away. Every character screen worth
 * copying (Diablo, WoW, every ARPG since) spins the *figure* on the spot for
 * exactly this reason: the room stays a room, the lighting keeps meaning what
 * it meant, and you still get all 360 degrees of the model.
 *
 * So: horizontal drag spins the figure, vertical drag raises and lowers the
 * camera a little, the wheel dollies. Only the middle one moves the camera at
 * all, and it is clamped to a range that keeps the figure in the niche.
 *
 * ## Why this file is separate and pure
 *
 * `character-stage.ts` owns a WebGL context, a resize observer and an
 * animation loop; none of that is needed to check that a flick decays, that
 * zoom clamps, or that a spin wraps rather than growing without bound. This
 * is the arithmetic, testable on its own — the same split `sim/` uses
 * everywhere else in this codebase.
 */

/** Radians of spin per pixel of horizontal drag. A full turn is roughly a 700px sweep. */
export const SPIN_PER_PX = 0.009;
/** Metres of camera lift per pixel of vertical drag. Deliberately gentle. */
export const LIFT_PER_PX = 0.006;
/** How far the camera may rise above, or sink below, its resting height. */
export const LIFT_MIN_M = -0.55;
export const LIFT_MAX_M = 1.5;
/** Multiplier on the resting camera distance. Above 1 is further away. */
export const ZOOM_MIN = 0.55;
export const ZOOM_MAX = 1.45;
/** Zoom change per notch of wheel. `deltaY` is normalised to notches first. */
export const ZOOM_PER_NOTCH = 0.09;
/**
 * Fraction of spin velocity kept per second after the pointer lets go.
 *
 * Firm on purpose. Decay is exponential, so the stop time barely depends on
 * how hard the flick was — with these two numbers even a capped, maximum
 * flick is at rest in under a second. A character screen still turning four
 * seconds after you let go feels like it is ignoring you, not like it has
 * momentum.
 */
export const SPIN_DECAY_PER_S = 0.005;
/**
 * Below this the coast is over.
 *
 * Not merely a float guard (though it is that too — the velocity would
 * otherwise decay toward zero forever without reaching it). 0.15 rad/s is
 * about nine degrees a second: a turn nobody can see, and one that would keep
 * the render loop awake indefinitely to draw it.
 */
export const SPIN_REST_RAD_PER_S = 0.15;

export interface StageOrbit {
  /** Radians the figure is turned from facing the camera. */
  spin: number;
  /** Radians per second still being carried from a flick. */
  spinVelocity: number;
  /** Metres above the resting camera height. */
  lift: number;
  /** Multiplier on the resting camera distance. */
  zoom: number;
}

export function restingOrbit(): StageOrbit {
  return { spin: 0, spinVelocity: 0, lift: 0, zoom: 1 };
}

/** True when the view is exactly where a freshly-shown delver would leave it. */
export function isResting(orbit: StageOrbit): boolean {
  return orbit.spin === 0 && orbit.spinVelocity === 0 && orbit.lift === 0 && orbit.zoom === 1;
}

export function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

/**
 * Keeps `spin` in [-PI, PI).
 *
 * Not cosmetic: a stage left spinning by an idle player would otherwise add a
 * few radians a second forever, and `Math.sin` of a number that large loses
 * precision long before it loses meaning.
 */
export function wrapAngle(radians: number): number {
  if (!Number.isFinite(radians)) return 0;
  const wrapped = (radians + Math.PI) % (Math.PI * 2);
  return (wrapped < 0 ? wrapped + Math.PI * 2 : wrapped) - Math.PI;
}

/**
 * One drag step.
 *
 * `allowLift` is false for touch: the canvas keeps `touch-action: pan-y` so a
 * vertical swipe scrolls the hub rather than being swallowed by the stage,
 * and reacting to the same gesture we are deliberately letting through would
 * tilt the camera every time someone scrolled past.
 */
export function applyDrag(orbit: StageOrbit, dxPx: number, dyPx: number, allowLift: boolean): StageOrbit {
  const dx = Number.isFinite(dxPx) ? dxPx : 0;
  const dy = Number.isFinite(dyPx) ? dyPx : 0;
  return {
    ...orbit,
    spin: wrapAngle(orbit.spin + dx * SPIN_PER_PX),
    // Dragging up should raise the camera, and `dy` grows downward.
    lift: allowLift ? clamp(orbit.lift - dy * LIFT_PER_PX, LIFT_MIN_M, LIFT_MAX_M) : orbit.lift,
  };
}

/**
 * Wheel zoom.
 *
 * `deltaMode` matters: a trackpad reports pixels, a mouse wheel lines, and a
 * page-scroll wheel pages. Treating all three as pixels makes one notch of a
 * real wheel — which can report 100+ — jump the whole zoom range at once.
 */
export function applyWheel(orbit: StageOrbit, deltaY: number, deltaMode = 0): StageOrbit {
  if (!Number.isFinite(deltaY) || deltaY === 0) return orbit;
  const perNotch = deltaMode === 1 ? 3 : deltaMode === 2 ? 1 : 100;
  const notches = clamp(deltaY / perNotch, -3, 3);
  return { ...orbit, zoom: clamp(orbit.zoom + notches * ZOOM_PER_NOTCH, ZOOM_MIN, ZOOM_MAX) };
}

/** Pinch: the ratio between the current and previous finger distance. */
export function applyPinch(orbit: StageOrbit, ratio: number): StageOrbit {
  if (!Number.isFinite(ratio) || ratio <= 0) return orbit;
  return { ...orbit, zoom: clamp(orbit.zoom / ratio, ZOOM_MIN, ZOOM_MAX) };
}

/**
 * A flick, as radians per second, taken from the last movement before release.
 *
 * Capped: a stray 400px jump in one frame (a tab regaining focus mid-drag,
 * a synthetic event) would otherwise launch the figure into a blur.
 */
export function releaseWithFlick(orbit: StageOrbit, dxPx: number, dtSeconds: number): StageOrbit {
  if (!Number.isFinite(dxPx) || !Number.isFinite(dtSeconds) || dtSeconds <= 0) {
    return { ...orbit, spinVelocity: 0 };
  }
  const velocity = clamp((dxPx * SPIN_PER_PX) / dtSeconds, -12, 12);
  return { ...orbit, spinVelocity: Math.abs(velocity) < SPIN_REST_RAD_PER_S ? 0 : velocity };
}

/** Advances a coasting spin. A held pointer passes `spinVelocity: 0`, so this is a no-op then. */
export function stepInertia(orbit: StageOrbit, dtSeconds: number): StageOrbit {
  if (orbit.spinVelocity === 0) return orbit;
  const dt = Number.isFinite(dtSeconds) ? Math.max(0, Math.min(0.1, dtSeconds)) : 0;
  if (dt === 0) return orbit;
  const decayed = orbit.spinVelocity * Math.pow(SPIN_DECAY_PER_S, dt);
  return {
    ...orbit,
    spin: wrapAngle(orbit.spin + orbit.spinVelocity * dt),
    spinVelocity: Math.abs(decayed) < SPIN_REST_RAD_PER_S ? 0 : decayed,
  };
}
